import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import AirlineCallPopup from '../components/AirlineCallPopup';
import Logo from '../components/Logo';
import { useContact } from '../context/ContactContext';

const svg = (d) => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const ICONS = {
  calendar: svg('M7 3v3m10-3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z'),
  route: svg('M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm12-10a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM8 17h7a3 3 0 0 0 0-6H9a3 3 0 0 1 0-6h7'),
  user: svg('M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-7 9a7 7 0 0 1 14 0'),
  seat: svg('M7 4v9a2 2 0 0 0 2 2h7l2 5M7 11h8M6 20h5'),
  alert: svg('M12 9v4m0 4h.01M10.3 3.9 2.6 17.3A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.7L13.7 3.9a2 2 0 0 0-3.4 0z'),
  clock: svg('M12 7v5l3 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z'),
  check: svg('M5 12.5l4.5 4.5L19 7.5'),
  phone: svg('M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2'),
};

const CHANGE_TYPES = [
  { icon:'calendar', title:'Date & time changes', desc:'Move to an earlier or later flight on the same route, subject to seat availability and the fare rules on your ticket.' },
  { icon:'route', title:'Route or airport changes', desc:'Switch your origin, destination or connecting city where the airline allows the ticket to be reissued.' },
  { icon:'seat', title:'Cabin upgrades', desc:'Move from economy to premium economy, business or first by paying the fare difference when seats are available.' },
  { icon:'user', title:'Name corrections', desc:'Fix minor spelling errors on a ticket. Most airlines do not allow a ticket to be transferred to another person.' },
  { icon:'alert', title:'Airline schedule changes', desc:'If the airline moves or cancels your flight, we help you review the rebooking or refund options the airline offers.' },
  { icon:'clock', title:'Same-day changes', desc:'Some airlines and fare types allow switching to an earlier or later flight on your travel day. Eligibility varies.' },
];

const COST_FACTORS = [
  { title:'Fare type', desc:'Flexible and main-cabin fares usually allow changes. Basic economy and some promotional fares may not allow changes at all.' },
  { title:'Fare difference', desc:'If the new flight costs more than your original ticket, you pay the difference. Some airlines issue a credit when the new fare is lower.' },
  { title:'Airline change fee', desc:'Many airlines have dropped change fees on standard fares for domestic flights, but fees can still apply on some fares and international routes.' },
  { title:'Timing', desc:'Changing well before departure usually means more seats and lower fares. Changes close to departure can cost more.' },
];

const STEPS = [
  { n:'1', title:'Call with your booking', desc:'Have your confirmation code, ticket number or the last name on the booking ready.' },
  { n:'2', title:'We check the fare rules', desc:'An agent reviews the airline\'s change policy for your exact ticket and looks for new flight options.' },
  { n:'3', title:'You approve the cost', desc:'We explain any fare difference, airline fee and our service fee before anything is changed.' },
  { n:'4', title:'Get your new itinerary', desc:'Once the airline reissues the ticket, you receive an updated confirmation by email.' },
];

const CHECKLIST = [
  'Booking confirmation code (PNR)',
  'Ticket number, if you have it',
  'Full name of each traveler as on the ticket',
  'Your preferred new dates, times or route',
  'Payment card for any fare difference or fees',
];

const FAQS = [
  { q:'Can I change my airline ticket after booking?', a:'In most cases, yes. Whether a change is allowed, and what it costs, depends on the airline and the fare rules attached to your ticket. Basic economy fares are often the most restricted.' },
  { q:'How much does it cost to change a flight?', a:'The total can include the fare difference between your old and new flight, any airline change fee, and our service fee. We tell you the full amount before the change is made.' },
  { q:'Can I change a flight within 24 hours of booking?', a:'For flights to, from or within the U.S., airlines must either hold a reservation without payment for 24 hours or let you cancel within 24 hours for a full refund, when the booking is made at least 7 days before departure. If you need different flights, cancelling and rebooking in that window may be the cheapest option.' },
  { q:'What happens if the airline changes or cancels my flight?', a:'The airline will usually offer an alternative flight. Under U.S. Department of Transportation rules, if an airline cancels or significantly changes a flight to, from or within the U.S. and you do not accept the alternative, you are generally entitled to a refund. We can help you review your options.' },
  { q:'Can I transfer my ticket to someone else?', a:'Generally no. Most airline tickets are non-transferable. Small spelling corrections are often allowed, but changing the traveler on a ticket usually means booking a new ticket.' },
  { q:'Is Flyairlinebooking the airline?', a:'No. Flyairlinebooking is an independent travel agency. We are not affiliated with any airline. All change rules and airline fees are set by the airline that issued your ticket.' },
  { q:'Can you change a ticket I bought directly from the airline?', a:'Tickets bought directly from an airline are normally changed through that airline. We can explain your options and help you book a new itinerary if that works better for you.' },
];

const pageJsonLd = {
  '@context':'https://schema.org','@type':'WebPage',name:'Airline Flight Change Help',url:'https://flyairlinebooking.com/airline-change',
  description:'Get help changing an airline flight: dates, times, routes, cabin upgrades and airline schedule changes, explained by a live travel agent.',
  publisher:{ '@type':'Organization', name:'Flyairlinebooking', url:'https://flyairlinebooking.com' },
};

const faqJsonLd = {
  '@context':'https://schema.org','@type':'FAQPage',
  mainEntity: FAQS.map(f => ({ '@type':'Question', name:f.q, acceptedAnswer:{ '@type':'Answer', text:f.a } })),
};

function SectionHeading({ eyebrow, title, sub, align = 'left' }) {
  return (
    <div className={`mb-8 sm:mb-10 ${align === 'center' ? 'text-center mx-auto' : ''} max-w-2xl`}>
      <p className="text-sm font-semibold text-blue-600 mb-2">{eyebrow}</p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h2>
      {sub && <p className="mt-3 text-slate-600 text-base leading-relaxed">{sub}</p>}
    </div>
  );
}

export default function AirlineChangePage() {
  const [openFaq, setOpenFaq] = useState(0);
  const { contactSettings } = useContact();
  const tfn = contactSettings?.tfn || '+1-855-895-8503';
  const tfnHref = `tel:${tfn.replace(/[^\d+]/g, '')}`;

  return (
    <div className="bg-white">
      <AirlineCallPopup
        name="Airline Flight Changes"
        code="airline-change"
        logoNode={<Logo size="large" />}
        message="Need to change your flight? Speak with a live travel agent about dates, routes and fees — 24/7."
      />

      <SEO
        title="Airline Flight Change Help — Change Dates, Times & Routes"
        canonical="/airline-change"
        description="Need to change an airline flight? Flyairlinebooking's live agents explain the airline's change policy, fare difference and fees, and help you rebook. Available 24/7."
        keywords="airline change, change flight, change airline ticket, flight change fee, reschedule flight, airline schedule change, change flight date"
        jsonLd={[pageJsonLd, faqJsonLd]}
      />

      {/* ── Hero ────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-navy-950 via-navy-900 to-blue-800 text-white overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-[0.12]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 460" fill="none" aria-hidden="true">
          <path d="M-50 380 C 250 120, 650 80, 1250 220" stroke="white" strokeWidth="1.5" strokeDasharray="6 10" />
          <path d="M-50 300 C 350 40, 800 60, 1250 120" stroke="white" strokeWidth="1" strokeDasharray="4 12" />
        </svg>
        <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-blue-100 bg-white/10 ring-1 ring-white/15 rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Flight change agents available 24/7
            </p>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.1] tracking-tight">
              Need to change your flight? We'll help you sort it out
            </h1>
            <p className="mt-4 text-base sm:text-lg text-blue-100/90 leading-relaxed max-w-2xl">
              New dates, a different route or an airline schedule change — a live agent checks your fare rules, finds new options and tells you the full cost before anything changes.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href={tfnHref} className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-bold px-6 py-3.5 rounded-xl transition-colors">
                <span className="[&_svg]:w-5 [&_svg]:h-5">{ICONS.phone}</span>
                Call {tfn}
              </a>
              <Link to="/" className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl ring-1 ring-white/25 hover:bg-white/10 font-semibold transition-colors">
                Search new flights
              </Link>
            </div>
          </div>

          <div className="bg-white text-slate-800 rounded-3xl shadow-[0_20px_60px_-15px_rgba(9,28,48,0.45)] ring-1 ring-slate-900/5 p-6 sm:p-7">
            <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Have this ready when you call</h2>
            <p className="text-sm text-slate-500 mb-4">It helps us find your booking and check options faster.</p>
            <ul className="space-y-3">
              {CHECKLIST.map(item => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5 [&_svg]:w-3.5 [&_svg]:h-3.5">{ICONS.check}</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Independent agency notice ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="flex gap-3 items-start rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4 sm:p-5 text-sm text-slate-600 leading-relaxed">
          <svg className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 11v5M12 8h.01" /></svg>
          <p>
            <strong className="text-slate-800">Flyairlinebooking is an independent travel agency.</strong> We are not affiliated with, endorsed by, or associated with any airline. Change eligibility, airline fees and fare rules are set by the airline that issued your ticket.
          </p>
        </div>
      </section>

      {/* ── Types of changes ────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <SectionHeading
          eyebrow="What we can help with"
          title="Common airline changes"
          sub="Whether a change is possible depends on the airline and your fare. We check the rules for your exact ticket."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CHANGE_TYPES.map(item => (
            <div key={item.title} className="rounded-2xl ring-1 ring-slate-200 bg-white p-6">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">{ICONS[item.icon]}</div>
              <h3 className="font-display font-bold text-slate-900 text-lg mb-1.5">{item.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── What affects the cost ───────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <SectionHeading
              eyebrow="Change costs"
              title="What decides the price of a change"
              sub="There is no single change fee. The total depends on a few things, and we explain each one before you agree."
            />
            <div className="rounded-2xl bg-blue-600 text-white p-6">
              <h3 className="font-display font-bold text-lg mb-1.5">Booked in the last 24 hours?</h3>
              <p className="text-blue-100 text-sm leading-relaxed">For U.S. flights booked at least 7 days before departure, you may be able to cancel for a full refund within 24 hours and rebook. Call us before the window closes.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 content-start">
            {COST_FACTORS.map((f, i) => (
              <div key={f.title} className="bg-white rounded-2xl ring-1 ring-slate-200 p-6">
                <span className="text-xs font-bold text-blue-600">0{i + 1}</span>
                <h3 className="font-display font-bold text-slate-900 text-lg mt-1 mb-1.5">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <SectionHeading eyebrow="How it works" title="Changing your flight in four steps" align="center" />
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map(s => (
            <li key={s.n} className="rounded-2xl ring-1 ring-slate-200 p-6">
              <span className="w-10 h-10 rounded-full bg-slate-900 text-white font-display font-bold flex items-center justify-center mb-4">{s.n}</span>
              <h3 className="font-display font-bold text-slate-900 text-lg mb-1.5">{s.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Call banner ─────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto rounded-3xl bg-gradient-to-br from-navy-950 to-navy-800 text-white p-6 sm:p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-blue-300 mb-2">Plans changed?</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Talk to a flight change agent now</h2>
            <p className="mt-3 text-slate-300 leading-relaxed">We'll look up your booking, check the airline's rules and walk you through your options — no change is made until you approve the cost.</p>
          </div>
          <a href={tfnHref} className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-bold px-6 py-3.5 rounded-xl transition-colors shrink-0">
            <span className="[&_svg]:w-5 [&_svg]:h-5">{ICONS.phone}</span>
            {tfn}
          </a>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <SectionHeading eyebrow="FAQs" title="Flight change questions" align="center" />
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {FAQS.map((faq, i) => (
            <div key={faq.q}>
              <button
                type="button"
                aria-expanded={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full py-5 text-left flex justify-between items-center gap-4 font-semibold text-slate-900 hover:text-blue-700 transition-colors"
              >
                {faq.q}
                <span className={`w-7 h-7 rounded-full ring-1 ring-slate-300 flex items-center justify-center text-slate-500 shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
              </button>
              {openFaq === i && <p className="pb-5 -mt-1 text-slate-600 text-sm leading-relaxed pr-10">{faq.a}</p>}
            </div>
          ))}
        </div>
        <p className="text-center text-slate-500 text-sm mt-8">
          See our{' '}
          <Link to="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link>,{' '}
          <Link to="/service-fees" className="text-blue-600 hover:underline">Service Fees</Link> and{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">Terms & Conditions</Link>.
        </p>
      </section>
    </div>
  );
}
